import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { PayrollSettingsPageDocument } from 'generated/resources/graphql'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import PrimoboxActivationRow from 'modules/integrations_primobox/components/ActivationRow'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import canSeeIntegration from 'modules/marketplace/lib/canSeeIntegration'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { CompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import { usePayrollBundleSettings } from 'modules/payroll_bundle/hooks/usePayrollBundleSettings'
import TypesOfCompensation from 'modules/payroll_bundle/pages/settings/Content/TypesOfCompensation'

import PayrollPaywallGuard from '../PayrollPaywallGuard'
import CoffreFortNumerique from './Content/CoffreFortNumerique'
import ConnectedSupplements from './Content/ConnectedSupplements'
import EmployeeUpdates from './Content/EmployeeUpdates'
import EmployeesData from './Content/EmployeesData'
import Folders from './Content/Folders'
import Integrations from './Content/Integrations'
import PayrollPolicies from './Content/PayrollPolicies'
import ProfessionalSettings from './Content/ProfessionalSettings'

const PayrollBundleSettings = () => {
  const companyInstallations = useLazyRef(() => new CompanyInstallations()).current
  const [isPrimoboxEnabled, setPrimoboxEnabled] = useState(false)

  const { loading: loadingCollections } = useAsyncData(
    () =>
      Promise.all([
        companyInstallations.fetch(),
        contractVersions.fetch({ withCachedFetchParams: true }),
      ]),
    []
  )

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const result = useQuery(PayrollSettingsPageDocument, {
    variables: { companyId },
  })

  const features = useUnsafeMemo(
    () => result.data?.features?.featuresConnection.nodes || [],
    [result]
  )

  const hasFeature = useUnsafeCallback(
    (featureName: string) =>
      features.find((feature) => feature.name === featureName)?.enabled || false,
    [features]
  )

  useUnsafeEffect(() => {
    setPrimoboxEnabled(hasFeature('integrations_primobox_active'))
  }, [hasFeature, features])

  const { subscriptionAddon, isNotFound, loading: loadingSettings } = usePayrollBundleSettings()
  const { hasInhousePayroll } = useInhousePayroll()
  const hasProfessionals = hasFeature('factorial_professionals')

  const hasIntegrationInstalled =
    canSeeIntegration(integrationCodes.a3innuva, companyInstallations) ||
    canSeeIntegration(integrationCodes.a3nom, companyInstallations) ||
    canSeeIntegration(integrationCodes.datev, companyInstallations) ||
    canSeeIntegration(integrationCodes.zucchetti, companyInstallations) ||
    canSeeIntegration(integrationCodes.paierh, companyInstallations) ||
    canSeeIntegration(integrationCodes.yeap_paierh, companyInstallations) ||
    canSeeIntegration(integrationCodes.silae, companyInstallations) ||
    canSeeIntegration(integrationCodes.datev_api, companyInstallations) ||
    canSeeIntegration(integrationCodes.datev_lug_api, companyInstallations) ||
    canSeeIntegration(integrationCodes.silae_api, companyInstallations) ||
    canSeeIntegration(integrationCodes.gispaghe, companyInstallations)

  const showLoader = loadingSettings || loadingCollections

  const authorized = !isNotFound && !!subscriptionAddon

  const content = (
    <Box>
      <PayrollPolicies contractVersions={contractVersions} />
      {hasProfessionals && <ProfessionalSettings />}
      {(hasInhousePayroll || hasProfessionals) && <EmployeeUpdates />}
      <TypesOfCompensation />
      <ConnectedSupplements />
      {hasIntegrationInstalled && <Integrations installedIntegrations={companyInstallations} />}
      <EmployeesData />
      {!!subscriptionAddon && <Folders addon={subscriptionAddon} />}
      {hasFeature('coffre_fort_activable') && <CoffreFortNumerique />}
      {hasFeature('integrations_primobox_activable') && (
        <PrimoboxActivationRow enabled={isPrimoboxEnabled} onToggle={setPrimoboxEnabled} />
      )}
    </Box>
  )

  return (
    <PayrollPaywallGuard>
      <ResponsivePage.Body
        loading={showLoader}
        error={null}
        authorized={authorized}
        showHeader={false}
      >
        {content}
      </ResponsivePage.Body>
    </PayrollPaywallGuard>
  )
}

export default PayrollBundleSettings
